import axios from 'axios';
import { request, apiInstance } from '../apis';
import { buildAttendanceExportFileName } from 'utils/attendanceDashboardPeriod';
import { downloadBlob, parseContentDispositionFilename } from 'utils/downloadBlob';

export interface AttendanceUploadAccess {
  canUpload: boolean;
  canManageSettings: boolean;
  isAdmin: boolean;
  isAssignedUploader: boolean;
}

export interface AttendanceFileResult {
  fileName: string;
  rowsRead: number;
  parseErrors: number;
}

export interface AttendanceImportSummary {
  totalRowsRead: number;
  validRows: number;
  duplicateInBatch: number;
  duplicateInDatabase: number;
  unknownPersonnelIds: number;
  invalidRows: number;
}

export interface AttendancePunchRow {
  rowNumber: number;
  fileName: string;
  rawPersonnelId: string;
  mappedEpfNumber?: string;
  punchDateTime?: string;
  firstName?: string;
  status: string;
  error?: string;
}

export interface AttendanceExcelUploadResult {
  importToken: string;
  detectedMonth: number;
  detectedYear: number;
  detectedEndMonth: number;
  detectedEndYear: number;
  detectedPeriodLabel: string;
  expiresAt: string;
  files: AttendanceFileResult[];
  summary: AttendanceImportSummary;
  unknownPersonnelIds: string[];
  sampleRows: AttendancePunchRow[];
  warnings: string[];
}

export interface AttendanceImportConfirmResult {
  batchId: string;
  detectedMonth: number;
  detectedYear: number;
  detectedEndMonth: number;
  detectedEndYear: number;
  detectedPeriodLabel: string;
  imported: number;
  duplicatesSkipped: number;
  dailyRecordsProcessing?: boolean;
  message: string;
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const IMPORT_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;
const LARGE_BATCH_FILE_THRESHOLD = 20;

export interface AttendanceLastImport {
  lastImportAt?: string;
  lastImportMonth?: number;
  lastImportYear?: number;
  lastImportEndMonth?: number;
  lastImportEndYear?: number;
  lastImportPeriodLabel?: string;
  lastImportedRows: number;
}

export interface AttendanceOverview {
  lastImportAt?: string;
  lastImportMonth?: number;
  lastImportYear?: number;
  lastImportedRows: number;
  totalPunches: number;
  assignedUploadersCount: number;
  minimumOfficeDaysPerMonth: number;
}

export interface AttendanceUploadAssignee {
  userRoleId: string;
  employeeName: string;
  epfNumber: string;
  upn?: string;
  role: string;
  displayLabel: string;
}

export interface AttendanceUploadAssigneeOption {
  userRoleId: string;
  employeeName: string;
  epfNumber: string;
  designation?: string;
  role: string;
  displayLabel: string;
  isAlreadyAssigned: boolean;
  hasImplicitUploadAccess: boolean;
}

export interface AttendanceUploadAssigneeOptions {
  items: AttendanceUploadAssigneeOption[];
  totalCount: number;
}

export interface AttendanceSettings {
  minimumOfficeDaysPerMonth: number;
  fullDayHoursThreshold: number;
  shortDayHoursThreshold: number;
  uploadAssignees: AttendanceUploadAssignee[];
  updatedAt?: string;
  updatedByUpn?: string;
  updatedByDisplayName?: string;
}

export interface UpdateAttendanceSettingsPayload {
  minimumOfficeDaysPerMonth: number;
  fullDayHoursThreshold: number;
  shortDayHoursThreshold: number;
  uploadAssigneeUserRoleIds: string[];
}

export type AttendanceDayStatus =
  | 'Present'
  | 'ShortDay'
  | 'VeryShort'
  | 'Missing'
  | 'FullDayLeave'
  | 'HalfDayLeave';

export interface AttendanceDashboardRow {
  date: string;
  dayLabel: string;
  employeeId: string;
  employeeName: string;
  arrival?: string;
  departure?: string;
  hoursWorked?: string;
  status: AttendanceDayStatus;
}

export interface AttendanceDashboardSummary {
  periodLabel: string;
  dateRangeLabel: string;
  totalEmployees: number;
  belowCompliance: number | null;
  missingAttendance: number;
  onLeave: number;
  minimumOfficeDaysPerMonth: number;
  fullDayHoursThreshold: number;
  shortDayHoursThreshold: number;
}

export interface AttendanceDashboardResponse {
  summary: AttendanceDashboardSummary;
  items: AttendanceDashboardRow[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface AttendanceDashboardQuery {
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export type AttendanceEmployeeFilter = 'all' | 'on-leave' | 'below' | 'missing';

export type AttendanceEmployeeComplianceStatus = 'Compliant' | 'BelowThreshold';

export interface AttendanceEmployeeRow {
  employeeId: string;
  employeeName: string;
  presentDays: number;
  workingDaysInPeriod: number;
  fullDayLeaveCount: number;
  halfDayLeaveCount: number;
  totalHours: number;
  hoursMeetTarget: boolean;
  complianceStatus: AttendanceEmployeeComplianceStatus | null;
  missingDays: number;
  vehicleNumbers?: string[];
}

export interface AttendanceEmployeeSummary {
  totalEmployees: number;
  belowCompliance: number | null;
  missingEmployees: number;
  onLeaveEmployees: number;
  workingDaysInPeriod: number;
  minimumOfficeDaysPerMonth: number;
  fullDayHoursThreshold: number;
  shortDayHoursThreshold: number;
  isFullCalendarMonth: boolean;
  periodLabel: string;
  dateRangeLabel: string;
}

export interface AttendanceEmployeeResponse {
  summary: AttendanceEmployeeSummary;
  items: AttendanceEmployeeRow[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface AttendanceEmployeeQuery {
  search?: string;
  from?: string;
  to?: string;
  filter?: AttendanceEmployeeFilter;
  page?: number;
  pageSize?: number;
}

export interface AttendanceExportQuery {
  from?: string;
  to?: string;
  search?: string;
  filter?: AttendanceEmployeeFilter;
  employeeIds?: string[];
}

export type AttendanceDetailDayStatus =
  | 'Present'
  | 'ShortDay'
  | 'VeryShort'
  | 'Missing'
  | 'WFH'
  | 'FullDayLeave'
  | 'HalfDayLeave';

export interface EmployeeAttendanceDetailDay {
  date: string;
  arrivalTime?: string | null;
  departureTime?: string | null;
  hoursWorked?: string | null;
  status: AttendanceDetailDayStatus;
  isCorrected: boolean;
  correctionNote?: string | null;
  correctedByName?: string | null;
  correctedAt?: string | null;
}

export interface EmployeeAttendanceDetailResponse {
  epfNumber: string;
  employeeFullName: string;
  department?: string | null;
  employmentType?: string | null;
  officeDays: number;
  workingDaysInPeriod: number;
  requiredOfficeDays: number;
  leaveDays: number;
  fullDayLeaveCount: number;
  halfDayLeaveCount: number;
  missingRecords: number;
  wfhDays: number;
  isCompliant: boolean;
  fullDayHoursThreshold: number;
  shortDayHoursThreshold: number;
  records: EmployeeAttendanceDetailDay[];
  totalRecords: number;
  page: number;
  pageSize: number;
}

export interface AttendanceCorrectionPayload {
  date: string;
  arrivalTime?: string;
  departureTime?: string;
  correctionNote?: string;
  overrideStatus?: AttendanceDetailDayStatus;
}

export type AttendanceCalendarDayKind = 'WorkingDay' | 'Weekend' | 'PublicHoliday';

export interface AttendanceCalendarDay {
  date: string;
  kind: AttendanceCalendarDayKind;
  publicHolidayName?: string | null;
  status?: AttendanceDetailDayStatus;
  arrivalTime?: string | null;
  departureTime?: string | null;
  hoursWorked?: string | null;
  isCorrected: boolean;
}

export interface AttendanceCalendarSummary {
  officeDays: number;
  leaveDays: number;
  missingDays: number;
  totalHours: number;
}

export interface AttendanceCalendarResponse {
  epfNumber: string;
  employeeFullName: string;
  from: string;
  to: string;
  requiredOfficeDays: number;
  fullDayHoursThreshold: number;
  shortDayHoursThreshold: number;
  summary: AttendanceCalendarSummary;
  days: AttendanceCalendarDay[];
}

export const AttendanceTrackingService = {
  getMyUploadAccess: () =>
    request.get<AttendanceUploadAccess>('attendance-tracking/me/upload-access'),

  getOverview: () =>
    request.get<AttendanceOverview>('attendance-tracking/overview'),

  getLastImport: () =>
    request.get<AttendanceLastImport>('attendance-tracking/import/last-import'),

  getLastUpdate: () =>
    request.get<AttendanceLastImport>('attendance-tracking/last-update'),

  getSettings: () =>
    request.get<AttendanceSettings>('attendance-tracking/settings'),

  updateSettings: (payload: UpdateAttendanceSettingsPayload) =>
    request.put<AttendanceSettings>('attendance-tracking/settings', payload),

  searchUploadAssigneeOptions: (search: string, page = 1, pageSize = 20) =>
    request.get<AttendanceUploadAssigneeOptions>(
      'attendance-tracking/settings/upload-assignee-options',
      { params: { search, page, pageSize } }
    ),

  uploadExcel: (files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    return request.post<AttendanceExcelUploadResult>(
      'attendance-tracking/import/upload-excel',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: IMPORT_REQUEST_TIMEOUT_MS
      }
    );
  },

  confirmImport: (importToken: string) =>
    request.post<AttendanceImportConfirmResult>(
      'attendance-tracking/import/confirm',
      { importToken },
      { timeout: IMPORT_REQUEST_TIMEOUT_MS }
    ),

  getDashboard: (query: AttendanceDashboardQuery) =>
    request.get<AttendanceDashboardResponse>('attendance-tracking/dashboard', {
      params: {
        from: query.from,
        to: query.to,
        search: query.search?.trim() || undefined,
        page: query.page,
        pageSize: query.pageSize
      }
    }),

  getEmployeeSummary: (query: AttendanceEmployeeQuery) =>
    request.get<AttendanceEmployeeResponse>('attendance-tracking/employees', {
      params: {
        from: query.from,
        to: query.to,
        search: query.search?.trim() || undefined,
        filter: query.filter ?? 'all',
        page: query.page,
        pageSize: query.pageSize
      }
    }),

  getEmployeeAttendanceDetail: (
    epfNumber: string,
    from: string,
    to: string,
    status?: AttendanceDetailDayStatus,
    page = 1,
    pageSize = 20
  ) =>
    request.get<EmployeeAttendanceDetailResponse>(
      `attendance/employees/${epfNumber}/detail`,
      { params: { from, to, status, page, pageSize } }
    ),

  getEmployeeAttendanceCalendar: (epfNumber: string, from: string, to: string) =>
    request.get<AttendanceCalendarResponse>(
      `attendance/employees/${epfNumber}/calendar`,
      { params: { from, to } }
    ),

  getMyAttendanceCalendar: (from: string, to: string) =>
    request.get<AttendanceCalendarResponse>(`attendance/me/calendar`, {
      params: { from, to }
    }),

  saveAttendanceCorrection: (epfNumber: string, body: AttendanceCorrectionPayload) =>
    request.put<{ status: string }>(
      `attendance/employees/${epfNumber}/correction`,
      body
    ),

  exportAttendanceExcel: async (query: AttendanceExportQuery) => {
    const fallbackFileName =
      query.from && query.to
        ? buildAttendanceExportFileName(query.from, query.to)
        : 'attendance-export.xlsx';

    try {
      const response = await apiInstance.get<Blob>('attendance-tracking/export', {
        params: {
          from: query.from,
          to: query.to,
          search: query.search?.trim() || undefined,
          filter: query.filter ?? 'all',
          employeeIds:
            query.employeeIds && query.employeeIds.length > 0
              ? query.employeeIds.join(',')
              : undefined
        },
        responseType: 'blob'
      });

      const filename =
        parseContentDispositionFilename(
          response.headers['content-disposition'] as string | undefined
        ) ?? fallbackFileName;

      downloadBlob(response.data, filename);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
        const message = await error.response.data.text();
        try {
          const payload = JSON.parse(message) as { message?: string };
          throw new Error(payload.message ?? 'Unable to export attendance records.');
        } catch (parseError) {
          if (parseError instanceof Error && parseError.message !== message) {
            throw parseError;
          }
          throw new Error('Unable to export attendance records.');
        }
      }

      throw error;
    }
  },

  maxFileSizeBytes: MAX_FILE_SIZE_BYTES,
  largeBatchFileThreshold: LARGE_BATCH_FILE_THRESHOLD,
  importRequestTimeoutMs: IMPORT_REQUEST_TIMEOUT_MS
};
